import { useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Calendar, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';

export function HeroSection() {
  const { t } = useLanguage();
  const containerRef = useRef(null);

  return (
    <section
      ref={containerRef}
      className="relative min-h-[90vh] flex items-center overflow-hidden"
    >
      {/* Background layers */}
      <div className="absolute inset-0">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1615286922420-c6b348ffbd62"
            alt="Laser engraving"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-background/80" />
        </div>
        <div className="absolute inset-0 grid-pattern opacity-40" />

        {/* Animated laser beams — GPU optimized */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-1/4 -left-20 w-[600px] h-[2px] bg-primary/30 blur-[1px]"
            style={{ willChange: 'transform' }}
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', repeatDelay: 2 }}
          />
          <motion.div
            className="absolute top-2/3 -right-20 w-[400px] h-[2px] bg-accent/30 blur-[1px]"
            style={{ willChange: 'transform' }}
            animate={{ x: ['200%', '-100%'] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 3 }}
          />
          <motion.div
            className="absolute top-0 left-1/2 w-[2px] h-[300px] bg-primary/20 blur-[1px] origin-top rotate-45"
            style={{ willChange: 'opacity' }}
            animate={{ opacity: [0.1, 0.4, 0.1] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        {/* Glow orbs */}
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] rounded-full bg-accent/5 blur-[100px]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge
              variant="outline"
              className="mb-6 px-4 py-1.5 text-xs tracking-widest uppercase border-primary/30 text-primary bg-primary/5"
            >
              <Sparkles className="w-3 h-3 mr-2" />
              {t('hero.badge')}
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-6"
          >
            {t('hero.title').split('.').filter(Boolean).map((word, i) => (
              <span key={i}>
                <span className={i === 1 ? 'text-gradient-silver' : 'text-title'}>
                  {word.trim()}.
                </span>{' '}
                {i === 1 ? <br className="hidden lg:block" /> : null}
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-base md:text-lg text-body leading-relaxed mb-10 max-w-xl font-light"
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-wrap gap-4"
          >
            <Link to="/events">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2 text-sm tracking-wide px-6 shadow-silver sheen-effect laser-engraved">
                <Calendar className="w-4 h-4" />
                {t('hero.cta1')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/studio">
              <Button size="lg" variant="outline" className="gap-2 text-sm tracking-wide px-6 border-border text-title hover:bg-secondary">
                <Sparkles className="w-4 h-4" />
                {t('hero.cta2')}
              </Button>
            </Link>
            <Link to="/inquiry">
              <Button size="lg" variant="outline" className="gap-2 text-sm tracking-wide px-6 border-accent/30 text-accent hover:bg-accent/10">
                <Palette className="w-4 h-4" />
                {t('hero.cta3')}
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
